import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trophy } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { WhatDoesThisMean } from "../shared/WhatDoesThisMean";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export function ReferralTracker({ referralsMade, pendingCommissions, earnedCommissions, onRefresh }) {
  const { user } = useAuth();
  const isSabrina = user?.client_id === "SAB001";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ referred_name: "", referred_contact: "", estimated_deal_value: "" });
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    const fetchLeaderboard = () => {
      api.get("/client/referral-leaderboard").then(({ data }) => setLeaderboard(data)).catch(() => {});
    };
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 15000);
    return () => clearInterval(interval);
  }, [referralsMade]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/referral", { ...form, estimated_deal_value: parseFloat(form.estimated_deal_value) || 0 });
      toast.success("Referral submitted to IGC");
      setOpen(false);
      setForm({ referred_name: "", referred_contact: "", estimated_deal_value: "" });
      onRefresh && onRefresh();
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      if (e2.response?.status === 422) {
        toast.error("Estimated deal value cannot be negative");
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    }
  };

  const estimatedCommission = (parseFloat(form.estimated_deal_value) || 0) * 0.2;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-igc-surface border border-igc-gold/15 p-6 h-full" data-testid="referral-income-tracker">
      <p className="text-igc-muted text-xs uppercase tracking-widest mb-4">
        Referral Income Tracker
        <WhatDoesThisMean
          testId="referral-tracker-explainer"
          text="Refer another business owner to IGC's Ecommerce program. If they join and their store gets going, you earn 20% commission. 'Pending' means we're still waiting for that referral to close."
        />
      </p>
      <p className="text-igc-muted text-[11px] mb-4">Earn 20% of the setup fee for every business you refer to IGC's Ecommerce program. Your referral commission is paid once your referral's setup fee is received.</p>
      {isSabrina && (
        <p className="text-igc-goldText text-[11px] mb-4" data-testid="referral-funding-bonus-note">
          As a valued partner, you also earn 20% of the setup fee on every Business Funding deal you refer — in addition to your standard 20% Ecommerce referral commission.
        </p>
      )}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div><p className="font-serif text-xl text-igc-cream" data-testid="referrals-made-value">{referralsMade}</p><p className="text-igc-muted text-[10px]" title="How many businesses you've referred to IGC.">Referrals</p></div>
        <div><p className="font-serif text-xl text-igc-goldText" data-testid="pending-commissions-value">${pendingCommissions}</p><p className="text-igc-muted text-[10px]" title="Referral commission still awaiting a decision.">Pending</p></div>
        <div><p className="font-serif text-xl text-igc-success" data-testid="earned-commissions-value">${earnedCommissions}</p><p className="text-igc-muted text-[10px]" title="Referral commission already earned.">Earned</p></div>
      </div>
      <Button data-testid="open-referral-form-button" onClick={() => setOpen(true)} title="Opens a form to refer a new business owner to IGC's Ecommerce program." className="w-full bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black transition-colors">
        Refer a Business
      </Button>

      {leaderboard.length > 0 && (
        <div className="mt-5 pt-4 border-t border-igc-gold/10" data-testid="referral-leaderboard">
          <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
            <Trophy size={13} weight="duotone" className="text-igc-goldText" /> Top Referrers
          </p>
          <div className="space-y-1.5">
            {leaderboard.map((row, i) => (
              <div key={row.client_id} data-testid={`leaderboard-row-${row.client_id}`} className="space-y-1">
                <div className={`flex items-center justify-between text-xs ${row.client_id === user?.client_id ? "text-igc-goldText" : "text-igc-muted"}`}>
                  <span>#{i + 1} {row.client_id}{row.client_id === user?.client_id ? " (You)" : ""}</span>
                  <span>{row.referrals_made} referral{row.referrals_made === 1 ? "" : "s"} · ${row.earned_commissions.toLocaleString()} earned</span>
                </div>
                {row.is_top_referrer && (
                  <span data-testid={`top-referrer-badge-${row.client_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 border bg-igc-gold/10 text-igc-goldText border-igc-gold/40 whitespace-nowrap">
                    <Trophy size={11} weight="fill" /> Top Referrer{row.streak_weeks > 1 ? ` · ${row.streak_weeks}wk streak` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="referral-form-modal">
          <DialogHeader><DialogTitle className="font-serif">Refer a Business to IGC Ecommerce</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="referral-name-input" required placeholder="Referred contact name" title="The name of the business or contact you're referring." value={form.referred_name} onChange={(e) => setForm({ ...form, referred_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="referral-contact-input" required placeholder="Email or phone" title="An email or phone number IGC can use to reach this referral." value={form.referred_contact} onChange={(e) => setForm({ ...form, referred_contact: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <div>
              <label className="text-igc-muted text-[11px] flex items-center mb-1.5">
                Estimated Deal Value
                <WhatDoesThisMean
                  testId="referral-deal-value-explainer"
                  text="Enter your best estimate of what this referral's setup fee will be. Your 20% commission is calculated automatically from this number once the deal closes."
                />
              </label>
              <Input data-testid="referral-deal-value-input" type="number" min="0" placeholder="Estimated deal value ($)" title="Your best estimate of what this referral's setup fee will be." value={form.estimated_deal_value} onChange={(e) => setForm({ ...form, estimated_deal_value: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            </div>
            {estimatedCommission > 0 && (
              <p className="text-igc-goldText text-xs" data-testid="referral-estimated-commission">Estimated 20% commission: ${estimatedCommission.toLocaleString()}</p>
            )}
            <Button data-testid="referral-submit-button" type="submit" title="Submits this referral to IGC." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Submit Referral</Button>
          </form>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
